import React, { useContext } from 'react';
import { useLocation } from 'react-router';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { SP_FOOTER_EVENTS } from 'Utilities/analytics/analyticsFooter';
import { usePageTransitionState } from 'Utilities/animation';
import { vehicleType } from 'Utilities/helpers';
import routing from 'Utilities/routing';

import { Content } from 'LegacySBCRA/Content/Content';

import { VrmCheckerFooter } from '../../../components/vrm/VrmCheckerFooter/VrmCheckerFooter';
import { routes } from '../../../routes';
import Texts from '../../../texts.json';
import { usePageRefEvents } from '../common/Hooks/usePageRefEvents';
import { CUSTOMER_HUB_VALUATION_ROUTE } from '../customerHub/CustomerHubHelpers';

import FooterLinks from './Components/FooterLinks/FooterLinks';
import FooterReviews from './Components/FooterReviews/FooterReviews';

import styles from './Footer.scss';

const T = new MDText(Texts);

export const Footer = () => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const location = useLocation();

  const onVehicleLookupLoad = () => SP_FOOTER_EVENTS.FOOTER_VRM_CHECKER_LOADED({ label: location.pathname });
  const { setRef } = usePageRefEvents({ onRefTrigger: onVehicleLookupLoad });

  const {
    animate: transitView,
    animateFooter: animate,
  } = usePageTransitionState();

  const {
    hideFooter,
    showDisclaimer,
    showDisclaimerDetail,
    showTrackerDisclaimer,
    useUlezVrmChecker = false, ...route
  } = routing.getMatch(location.pathname, routes);

  if (
    hideFooter
    || (location.pathname === `${CUSTOMER_HUB_VALUATION_ROUTE}/${route?.params?.vrm}` && location.search.includes('redirect'))
  ) {
    return null;
  }

  const showVehicleLookup = !route.hideVehicleLookup;

  let showReviewsCarousel = !route.hideReviews;

  // hide review on valuation page
  if (route.vrmPath && vehicle?.enquiry?.id) {
    showReviewsCarousel = false;
  }

  const displayText = {
    get shouldShowLookupButtonChevron() {
      return true;
    },

    get vehicleLookupButtonText() {
      return T.translate('footerView.vehicleLookup.buttonLabel');
    },

    get vehicleLookupTitle() {
      if (route?.params?.brand || location.pathname === '/' || route?.params?.partner) {
        return T.translate('footerView.vehicleLookup.homeTitle');
      }

      if (location.pathname === '/how-it-works') {
        return T.translate('footerView.vehicleLookup.howItWorksTitle');
      }

      return T.translate('footerView.vehicleLookup.title', { vehicleType: vehicleType(location.pathname) });
    },
  };

  return (
    <footer
      className={cx({
        [styles.hidden]: transitView,
        [styles.animate]: animate,
      })}
    >
      {showReviewsCarousel
        ? <FooterReviews />
        : null}
      {showVehicleLookup
        ? (
          <Content
            ref={(ref) => setRef(ref || null)}
            center
            className={styles.vehicleLookup}
            element="section"
            wrapper={{
              className: styles.vehicleLookupWrapper,
            }}
          >
            <div className={styles.vehicleLookupInner}>
              {!useUlezVrmChecker && (
                <p data-lookup-title className="mw-i">
                  <span data-nosnippet data-cy="footerVrmTitle">
                    {displayText.vehicleLookupTitle}
                  </span>
                </p>
              )}
              <VrmCheckerFooter
                {...{ useUlezVrmChecker }}
                large
                analyticsEvents={{
                  vrmSubmittedSuccessfully: () => SP_FOOTER_EVENTS.FOOTER_VRM_CHECKER_SUBMITTED({
                    buttonLabel: displayText.vehicleLookupButtonText as string,
                    label: location.pathname,
                  }),
                }}
                buttonProps={{
                  className: ' mw-button-large',
                  label: displayText.vehicleLookupButtonText,
                }}
                chevron={displayText.shouldShowLookupButtonChevron}
                e2eName="footer vrm checker"
                id="footer-vrm"
                inputClassName={styles.footerVrmChecker}
              />
            </div>
          </Content>
        )
        : null}
      <FooterLinks {...{ showDisclaimer, showDisclaimerDetail, showTrackerDisclaimer }} />
    </footer>
  );
};

export default Footer;
